import { find } from 'lodash-es'
import { useHead } from '@unhead/vue'
import { computed, unref } from 'vue'

// 舊版函數，保留向後相容性
export const seoTitleDescription = ({ title, description }) => {
  return [
    { name: 'title', content: title },
    { name: 'og:title', content: title },
    { name: 'description', content: description },
    { name: 'og:description', content: description },
  ]
}

// 舊版函數，保留向後相容性
export const formatSeoMeta = ({
  title,
  description,
  ogTitle,
  ogDescription,
  ogImg,
  ogImgAlt,
  ogUrl,
}) => {
  const metas = [
    { name: 'title', content: `${title} - 威許移動` },
    { name: 'og:title', content: `${ogTitle || title} - 威許移動` },
    { property: 'og:title', content: `${ogTitle || title} - 威許移動` },

    {
      name: 'og:url',
      content: window.location.href,
    },
    {
      property: 'og:url',
      content: window.location.href,
    },

    {
      name: 'og:image',
      content: `https://www.beautinq.com/og-image.png`,
    },
    {
      property: 'og:image',
      content: `https://www.beautinq.com/og-image.png`,
    },

    { name: 'og:image:alt', content: `美業歐巴｜為美業打造的即時預約系統及經營管理工具` },
    { property: 'og:image:alt', content: `美業歐巴｜為美業打造的即時預約系統及經營管理工具` },

    { name: 'og:site_name', content: `威許移動：美業歐巴` },
    { property: 'og:site_name', content: `威許移動：美業歐巴` },

    { name: 'copyright', content: '威許移動股份有限公司' },
    { property: 'copyright', content: '威許移動股份有限公司' },

    { name: 'company', content: '威許移動股份有限公司' },
    { property: 'company', content: '威許移動股份有限公司' },

    { name: 'author', content: '威許移動股份有限公司' },
    { property: 'author', content: '威許移動股份有限公司' },
  ]
  if (description || ogDescription) {
    metas.push({ name: 'description', content: description || ogDescription })
    metas.push({
      name: 'og:description',
      content: ogDescription || description,
    })
    metas.push({
      property: 'og:description',
      content: ogDescription || description,
    })
  }

  if (ogImg) {
    const nameOg = find(metas, { name: 'og:image' })
    const propertyOg = find(metas, { property: 'og:image' })
    if (nameOg) nameOg.content = ogImg
    if (propertyOg) propertyOg.content = ogImg
  }
  if (ogImgAlt) {
    const nameOg = find(metas, { name: 'og:image:alt' })
    const propertyOg = find(metas, { property: 'og:image:alt' })
    if (nameOg) nameOg.content = ogImgAlt
    if (propertyOg) propertyOg.content = ogImgAlt
  }
  if (ogUrl) {
    const nameOg = find(metas, { name: 'og:url' })
    const propertyOg = find(metas, { property: 'og:url' })
    if (nameOg) nameOg.content = ogUrl
    if (propertyOg) propertyOg.content = ogUrl
  }
  return {
    title: `${title} - 威許移動`,
    meta: metas,
    htmlAttrs: {
      lang: 'zh-Hant-TW',
      amp: true,
    },
  }
}

/**
 * 新的 Unhead 版本 SEO Composable
 * 支援響應式資料更新
 * 必須在 setup() 函數中調用
 *
 * @param {Object} options - SEO 設定選項（支援 ref 或 computed）
 * @param {String|Ref|ComputedRef} options.title - 頁面標題
 * @param {String|Ref|ComputedRef} options.description - 頁面描述
 * @param {String|Ref|ComputedRef} options.ogTitle - Open Graph 標題
 * @param {String|Ref|ComputedRef} options.ogDescription - Open Graph 描述
 * @param {String|Ref|ComputedRef} options.ogImg - Open Graph 圖片 URL
 * @param {String|Ref|ComputedRef} options.ogImgAlt - Open Graph 圖片替代文字
 * @param {String|Ref|ComputedRef} options.ogUrl - Open Graph URL
 */
export const useSEO = ({
  title,
  description,
  ogTitle,
  ogDescription,
  ogImg = 'https://www.beautinq.com/og-image.png',
  ogImgAlt = '美業歐巴｜為美業打造的即時預約系統及經營管理工具',
  ogUrl,
}) => {
  // 使用 computed 確保響應式更新
  const fullTitle = computed(() => `${unref(title)} - 威許移動`)
  const fullOgTitle = computed(
    () => `${unref(ogTitle) || unref(title)} - 威許移動`,
  )
  const finalOgUrl = computed(() => unref(ogUrl) || window.location.href)
  // canonical 不帶 query string，避免 ?xxx= 被 Google 當成不同的重複網址
  const canonicalUrl = computed(
    () => unref(ogUrl) || `${window.location.origin}${window.location.pathname}`,
  )
  const finalDescription = computed(
    () => unref(description) || unref(ogDescription) || '',
  )
  const finalOgDescription = computed(
    () => unref(ogDescription) || unref(description) || '',
  )
  const finalOgImg = computed(() => unref(ogImg))
  const finalOgImgAlt = computed(() => unref(ogImgAlt))

  // 在 setup 階段調用 useHead，傳入 computed values
  useHead({
    title: fullTitle,
    htmlAttrs: {
      lang: 'zh-Hant-TW',
    },
    meta: [
      { name: 'title', content: fullTitle },
      { name: 'description', content: finalDescription },
      { property: 'og:title', content: fullOgTitle },
      { property: 'og:description', content: finalOgDescription },
      { property: 'og:image', content: finalOgImg },
      { property: 'og:image:alt', content: finalOgImgAlt },
      { property: 'og:url', content: finalOgUrl },
      { property: 'og:site_name', content: '威許移動：美業歐巴' },
      { property: 'og:type', content: 'website' },
      { name: 'copyright', content: '威許移動股份有限公司' },
      { name: 'company', content: '威許移動股份有限公司' },
      { name: 'author', content: '威許移動股份有限公司' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: fullOgTitle },
      { name: 'twitter:description', content: finalOgDescription },
      { name: 'twitter:image', content: finalOgImg },
    ],
    link: [{ rel: 'canonical', href: canonicalUrl }],
  })
}

/**
 * 簡化版的 SEO Composable
 * 支援響應式資料更新
 * 必須在 setup() 函數中調用
 *
 * @param {Object} options - SEO 設定選項（支援 ref 或 computed）
 * @param {String|Ref|ComputedRef} options.title - 頁面標題
 * @param {String|Ref|ComputedRef} options.description - 頁面描述
 */
export const useSEOSimple = ({ title, description }) => {
  const fullTitle = computed(() => `${unref(title)} - 威許移動`)
  const finalDescription = computed(() => unref(description))
  const finalOgUrl = computed(() => window.location.href)

  useHead({
    title: fullTitle,
    htmlAttrs: {
      lang: 'zh-Hant-TW',
    },
    meta: [
      { name: 'title', content: fullTitle },
      { name: 'description', content: finalDescription },
      { property: 'og:title', content: fullTitle },
      { property: 'og:description', content: finalDescription },
      { property: 'og:url', content: finalOgUrl },
    ],
  })
}
